import { useEffect, useState } from 'react';

function matches(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;
}

/** Whether a CSS media query holds, kept current as the window is resized. */
export function useMediaQuery(query: string): boolean {
  const [holds, setHolds] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = () => setHolds(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return holds;
}

/**
 * The three widths a page lays itself out for, on Tailwind's breakpoints:
 * phone below `md` (768px), tablet below `lg` (1024px), desktop from there.
 *
 * Where `matchMedia` does not exist (jsdom in the tests) this is desktop, the
 * layout every page had before it learned the other two.
 */
export type Layout = 'phone' | 'tablet' | 'desktop';

export function useLayout(): Layout {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const desktop = useMediaQuery('(min-width: 1024px)');
  const tablet = useMediaQuery('(min-width: 768px)');
  if (!supported || desktop) return 'desktop';
  return tablet ? 'tablet' : 'phone';
}
